import moment from 'moment';

/**
 * Format date for display using a consistent, readable format
 * @param date - Date string, Date object, or moment object
 * @param format - Optional format string (default: 'MMM DD, YYYY')
 * @returns Formatted date string
 */
export function formatDate(
  date: string | Date | moment.Moment,
  format: string = 'MMM DD, YYYY'
): string {
  return moment(date).format(format);
}

/**
 * Format date and time for display
 * @param date - Date to format
 * @param format - Optional format string (default: 'MMM DD, YYYY hh:mm A')
 * @returns Formatted date and time string
 */
export function formatDateTime(
  date: string | Date | moment.Moment,
  format: string = 'MMM DD, YYYY hh:mm A'
): string {
  return moment(date).format(format);
}

/**
 * Format date relative to current time (e.g., "2 hours ago", "in 3 days")
 * @param date - Date to compare
 * @param withoutSuffix - Whether to include "ago" or "in"
 * @returns Relative time string
 */
export function formatRelativeDate(
  date: string | Date | moment.Moment,
  withoutSuffix = false
): string {
  return moment(date).fromNow(withoutSuffix);
}

/**
 * Check if a date is in the past
 * @param date - Date to check
 * @returns Boolean indicating if date is in the past
 */
export function isPastDate(date: string | Date | moment.Moment): boolean {
  return moment(date).isBefore(moment());
}

/**
 * Check if a date is in the future
 * @param date - Date to check
 * @returns Boolean indicating if date is in the future
 */
export function isFutureDate(date: string | Date | moment.Moment): boolean {
  return moment(date).isAfter(moment());
}

/**
 * Calculate days between two dates
 * @param from - Start date
 * @param to - End date (defaults to current date)
 * @returns Number of days difference
 */
export function getDaysDifference(
  from: string | Date | moment.Moment,
  to: string | Date | moment.Moment = moment()
): number {
  const start = moment(from);
  const end = moment(to);
  return end.diff(start, 'days');
}

/**
 * Add days to a date
 * @param date - Base date
 * @param days - Number of days to add
 * @returns New moment object
 */
export function addDays(date: string | Date | moment.Moment, days: number): moment.Moment {
  return moment(date).add(days, 'days');
}

/**
 * Subtract days from a date
 * @param date - Base date
 * @param days - Number of days to subtract
 * @returns New moment object
 */
export function subtractDays(date: string | Date | moment.Moment, days: number): moment.Moment {
  return moment(date).subtract(days, 'days');
}

/**
 * Check if two dates are the same day
 * @param date1 - First date
 * @param date2 - Second date
 * @returns Boolean indicating if dates are the same day
 */
export function isSameDay(
  date1: string | Date | moment.Moment,
  date2: string | Date | moment.Moment
): boolean {
  return moment(date1).isSame(date2, 'day');
}

/**
 * Get start of month
 * @param date - Date to get month start for
 * @returns Month start date
 */
export function getStartOfMonth(date: string | Date | moment.Moment): moment.Moment {
  return moment(date).startOf('month');
}

/**
 * Get end of month
 * @param date - Date to get month end for
 * @returns Month end date
 */
export function getEndOfMonth(date: string | Date | moment.Moment): moment.Moment {
  return moment(date).endOf('month');
}

/**
 * Format date for input fields (YYYY-MM-DD)
 * @param date - Date to format
 * @returns Formatted date string for HTML input
 */
export function formatForInput(date: string | Date | moment.Moment): string {
  return moment(date).format('YYYY-MM-DD');
}

/**
 * Parse date from input field
 * @param dateString - Date string from input field
 * @returns Moment object
 */
export function parseInputDate(dateString: string): moment.Moment {
  return moment(dateString, 'YYYY-MM-DD');
}

/**
 * Get current timestamp
 * @returns Current unix timestamp
 */
export function getCurrentTimestamp(): number {
  return moment().unix();
}

/**
 * Format time duration in human readable format
 * @param hours - Hours
 * @param minutes - Minutes (optional)
 * @returns Formatted time string
 */
export function formatDuration(hours: number, minutes: number = 0): string {
  const totalMinutes = hours * 60 + minutes;
  const durationHours = Math.floor(totalMinutes / 60);
  const durationMins = totalMinutes % 60;

  if (durationHours > 0) {
    return durationMins > 0
      ? `${durationHours}h ${durationMins}m`
      : `${durationHours}h`;
  }
  return `${durationMins}m`;
}

/**
 * Get business days between two dates (excluding weekends)
 * @param from - Start date
 * @param to - End date
 * @returns Number of business days
 */
export function getBusinessDays(
  from: string | Date | moment.Moment,
  to: string | Date | moment.Moment
): number {
  const start = moment(from);
  const end = moment(to);
  const diffDays = end.diff(start, 'days');
  const weeks = Math.floor(diffDays / 7);
  const remainderDays = diffDays % 7;

  return weeks * 5 + Math.max(0, remainderDays - Math.max(0, remainderDays - 5));
}
